import {type DefaultError, useMutation, useQueryClient} from "@tanstack/react-query";
import {queryHttp} from "~/query/queryHttp";
import {notificationStore} from "~/stores/init";
import {
    BU_QUALIFICATION_URL_PREFIX,
    COMPLIANCE_ARTIFACT_URL_PREFIX,
    COMPLIANCE_REQUIREMENT_URL_PREFIX,
    complianceArtifactCollectionUrl,
} from "~/pages/supplier-360/services/supplier360Urls";
import type {
    ComplianceArtifact,
    ComplianceArtifactWrite,
} from "~/pages/supplier-360/types/Supplier360Types";

/** `multipart/form-data` body — `null` dates are left out rather than sent as the string "null". */
function toFormData(payload: ComplianceArtifactWrite): FormData {
    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
        if (value === null || value === undefined) return;
        formData.append(key, value instanceof File ? value : String(value));
    });
    return formData;
}

/**
 * `POST supplier_360/compliance_artifact/` — the "Upload evidence" form (§2.9), with the file as
 * `multipart/form-data`.
 *
 * Creating evidence flips the requirement to `met` server-side, so this refreshes the requirement
 * queries (detail, grid, KPIs) and the BU qualifications (whose `status` follows, §2.7) along with
 * the evidence list.
 */
export function useCreateComplianceArtifactMutation() {
    const queryClient = useQueryClient();

    return useMutation<ComplianceArtifact, DefaultError, ComplianceArtifactWrite>({
        mutationFn: async (payload) => {
            const {data} = await queryHttp.post<ComplianceArtifact>(
                complianceArtifactCollectionUrl(),
                toFormData(payload),
                // Overrides the instance's JSON default, which would otherwise serialize the FormData.
                {headers: {'Content-Type': 'multipart/form-data'}},
            );
            return data;
        },
        onSuccess: (created) => {
            void queryClient.invalidateQueries({
                predicate: (query) => {
                    const key = query.queryKey[0];
                    return typeof key === 'string'
                        && (key.startsWith(COMPLIANCE_ARTIFACT_URL_PREFIX)
                            || key.startsWith(COMPLIANCE_REQUIREMENT_URL_PREFIX)
                            || key.startsWith(BU_QUALIFICATION_URL_PREFIX));
                },
            });
            notificationStore.show(`${created.name} uploaded.`, {
                severity: 'success',
                id: `compliance-artifact-add-success-${created.satisfies_requirement}`,
                autoHideDuration: 3000,
            });
        },
        onError: (_error, {satisfies_requirement}) => {
            notificationStore.show('Could not upload the evidence. Please try again.', {
                severity: 'error',
                id: `compliance-artifact-add-error-${satisfies_requirement}`,
                autoHideDuration: 4000,
            });
        },
    });
}
